<template>
  <v-container fluid>
    <v-row class="flex-row justify-space-between">
      <v-col cols="6">
        <v-breadcrumbs :items="breadcrumbs" />
      </v-col>
      <v-col cols="6" class="text-end">
        <history-dialog
          model="Person"
          :_id="id"
          v-bubble.error
          v-bubble.info
        />
      </v-col>
    </v-row>

    <v-alert
      type="info"
      :value="person.sourceOfTruth !== SourceOfTruth.MSA"
    >
      <v-row align="center">
        The source of truth for this person is {{ person.sourceOfTruth }}. Details set here may be overwritten by system processes.
      </v-row>
    </v-alert>

    <v-alert
      type="info"
      :value="person.matchedPersons.length > 0"
    >
      <v-row align="center">
        <v-col class="grow">
          This person has {{ person.matchedPersons.length }} potential duplicates.
        </v-col>
        <v-col class="shrink">
          <v-btn
            small
            outlined
            @click="duplicatesDialog = true"
          >
            Consolidate
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <v-row>
      <v-col cols="12">
        <h6 class="text-h6">Details</h6>
      </v-col>

      <v-col cols="12" lg="6" xl="4">
        <!-- Details -->
        <v-card color="teal lighten-5">
          <v-card-title>
            Personal Details
          </v-card-title>

          <v-card-text>
            <v-simple-table dense class="pa-2">
              <template v-slot:default>
                <tr>
                  <th class="text-left shrink">MSA Person ID</th>
                  <td class="text-left grow pl-5">{{ person.personId }}</td>
                </tr>
                <tr>
                  <th class="text-left shrink">Name</th>
                  <td class="text-left grow pl-5">{{ person.name }}</td>
                </tr>
                <tr>
                  <th class="text-left shrink">Given name</th>
                  <td class="text-left grow pl-5">{{ person.givenName }}</td>
                </tr>
                <tr>
                  <th class="text-left shrink">Family name</th>
                  <td class="text-left grow pl-5">{{ person.familyName }}</td>
                </tr>
                <tr>
                  <th class="text-left shrink">Email</th>
                  <td class="text-left grow pl-5">{{ person.email }}</td>
                </tr>
                <tr>
                  <th class="text-left shrink">Phone</th>
                  <td class="text-left grow pl-5">{{ person.phoneNumber }}</td>
                </tr>
                <tr>
                  <th class="text-left shrink">Created at</th>
                  <td class="text-left grow pl-5">{{ person.createdAt | formatDate }}</td>
                </tr>
                <tr>
                  <th class="text-left shrink">Updated at</th>
                  <td class="text-left grow pl-5">{{ person.updatedAt | formatDate }}</td>
                </tr>
              </template>
            </v-simple-table>
          </v-card-text>

          <v-card-actions>
            <v-btn
              color="primary"
              outlined
              @click="editDetails"
            >
              Edit
            </v-btn>
            <v-dialog
              v-model="detailsDialog"
              width="800"
            >
              <v-card>
                <v-card-title>
                  Details
                </v-card-title>
                <v-card-text>
                  <person-form
                    v-model="detailsForm"
                    @valid="valid = $event"
                  />
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                  <v-btn
                    color="primary"
                    text
                    @click="saveDetails"
                    :disabled="!valid"
                  >
                    Save
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    @click="detailsDialog = false"
                  >
                    Cancel
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card-actions>
        </v-card>
        <!-- /Details -->
      </v-col>

      <v-col cols="12" lg="6" xl="4">
        <!-- Flags -->
        <v-card color="teal lighten-5">
          <v-card-title>
            Account Flags
          </v-card-title>

          <v-card-text>
            <v-form>
              <v-checkbox
                dense
                label="Account Active"
                :value="person.isActive"
                @change="toggleFlag('isActive', $event)"
              />
              <v-checkbox
                dense
                label="MSA Employee"
                :value="person.isMsaEmployee"
                @change="toggleFlag('isMsaEmployee', $event)"
              />
              <v-checkbox
                dense
                label="MSA Employee - Current"
                :value="person.isCurrentMsaEmployee"
                @change="toggleFlag('isCurrentMsaEmployee', $event)"
              />
              <v-checkbox
                dense
                label="MLA Employee"
                :value="person.isMlaEmployee"
                @change="toggleFlag('isMlaEmployee', $event)"
              />
              <v-checkbox
                dense
                label="MLA Employee - Current"
                :value="person.isCurrentMlaEmployee"
                @change="toggleFlag('isCurrentMlaEmployee', $event)"
              />
              <v-checkbox
                dense
                label="AUS-MEAT Auditor"
                :value="person.isAusMeatAuditor"
                @change="toggleFlag('isAusMeatAuditor', $event)"
              />
              <v-checkbox
                dense
                label="AUS-MEAT Staff"
                :value="person.isAusMeatStaff"
                @change="toggleFlag('isAusMeatStaff', $event)"
              />
              <v-checkbox
                dense
                label="MSA System Administrator"
                :value="person.isMsaAdministrator"
                @change="toggleFlag('isMsaAdministrator', $event)"
              />
            </v-form>
          </v-card-text>
        </v-card>
        <!-- /Flags -->
      </v-col>

      <!-- Registrations -->
      <v-col cols="12" lg="6" xl="4">
        <v-card color="teal lighten-5">
          <v-card-title>
            Members
          </v-card-title>

          <v-card-text>
            <v-simple-table class="pa-2">
              <template v-slot:default>
                <tr>
                  <th class="text-left">Member number</th>
                  <th class="text-left">Member name</th>
                  <th class="text-left">Position</th>
                </tr>
                <tr v-for="linkage of person.registrations" :key="linkage._id">
                  <td class="text-left">{{ linkage.registration.memberNumber }}</td>
                  <td class="text-left">{{ linkage.registration.memberName }}</td>
                  <td class="text-left">{{ linkage.position }}</td>
                </tr>
                <tr v-if="!person.registrations.length">
                  <td colspan="2" class="text-center">This person is not associated with any members.</td>
                </tr>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- /Registrations -->

      <!-- Login -->
      <v-col cols="12" lg="6" xl="4">
        <v-card color="teal lighten-5">
          <v-card-title>
            Login
          </v-card-title>

          <v-card-text>
            <person-identity
              :personId="id"
              v-bubble.error
              v-bubble.info
            />
          </v-card-text>
        </v-card>
      </v-col>
      <!-- /Login -->
    </v-row>

    <v-row>
      <v-col cols="12">
        <h6 class="text-h6">Training</h6>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card color="teal lighten-5">
          <v-card-title>
            Training Assessments
          </v-card-title>
          <v-card-text>
            <trainee-assessment-table
              :personId="id"
              v-bubble.error
              v-bubble.info
            />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="6">
        <v-card outlined color="teal lighten-5">
          <v-card-title>
            Training Sessions
          </v-card-title>
          <v-card-text>
            <trainee-sessions-table
              :personId="id"
              v-bubble.error
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="duplicatesDialog" max-width="1800">
      <v-card min-height="90vh">
        <v-card-title>
          Detected Duplicates
        </v-card-title>

        <v-card-text>
          <person-duplicate-list
            v-if="duplicatesDialog"
            :personId="id"
            v-bubble.error
            v-bubble.info
            @close="duplicatesDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import { SourceOfTruth } from '@mymsa/common'

import HistoryDialog from '../components/HistoryDialog.vue'
import PersonIdentity from '../components/PersonIdentity.vue'
import TraineeAssessmentTable from '../components/TraineeAssessmentTable.vue'
import TraineeSessionsTable from '../components/TraineeSessionsTable.vue'
import PersonForm from '../components/forms/PersonForm.vue'
import PersonDuplicateList from '../components/PersonDuplicateList.vue'

export default {
  name: 'AdminPersonView',

  props: {
    id: {
      type: String,
      required: true
    }
  },

  components: {
    HistoryDialog,
    PersonIdentity,
    TraineeAssessmentTable,
    TraineeSessionsTable,
    PersonForm,
    PersonDuplicateList
  },

  data () {
    return {
      SourceOfTruth,
      person: {
        sourceOfTruth: SourceOfTruth.MSA,
        registrations: [],
        matchedPersons: []
      },
      detailsDialog: false,
      detailsForm: {},
      valid: false,
      duplicatesDialog: false
    }
  },

  computed: {
    breadcrumbs () {
      return [
        { text: 'People', exact: true, to: { name: 'AdminPersonList' } },
        { text: this.person.name || this.$route.meta?.title || this.$route.name }
      ]
    }
  },

  apollo: {
    person: {
      query: gql`query GetPerson($id: MongoID!) {
        person: personById(_id: $id) {
          _id
          personId
          name
          givenName
          familyName
          email
          phoneNumber
          createdAt
          updatedAt
          sourceOfTruth
          isActive
          isMsaEmployee
          isCurrentMsaEmployee
          isMlaEmployee
          isCurrentMlaEmployee
          isAusMeatAuditor
          isAusMeatStaff
          isMsaAdministrator
          registrations {
            _id
            position
            registration {
              _id
              memberNumber
              memberName
            }
          }
          matchedPersons {
            _id
          }
        }
      }`,
      variables () {
        return {
          id: this.id
        }
      },
      error (e) {
        this.onError(e)
      }
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    },

    editDetails () {
      this.detailsForm = Object.assign({}, this.person)
      this.detailsDialog = true
    },

    async saveDetails () {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdPersonInput!) {
            personUpdateById(_id: $id, record: $record){
              record {
                _id
                givenName
                familyName
                name
                email
                phoneNumber
                updatedAt
              }
            }
          }`,
          variables: {
            id: this.id,
            record: {
              givenName: this.detailsForm.givenName,
              familyName: this.detailsForm.familyName,
              name: this.detailsForm.name,
              email: this.detailsForm.email,
              phoneNumber: this.detailsForm.phoneNumber
            }
          }
        })
      } catch (e) {
        this.onError(e)
        return
      }
      this.detailsDialog = false
    },

    async toggleFlag (key, value) {
      const _value = Boolean(value)
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdPersonInput!) {
            personUpdateById(_id: $id, record: $record){
              record {
                _id
                isActive
                isMsaEmployee
                isCurrentMsaEmployee
                isMlaEmployee
                isCurrentMlaEmployee
                isAusMeatAuditor
                isAusMeatStaff
                isMsaAdministrator
              }
            }
          }`,
          variables: {
            id: this.id,
            record: {
              [key]: _value
            }
          }
        })
      } catch (e) {
        this.onError(e)
        return
      }
      const message = `${this.person.name} ${key} flag is now ${_value}.`
      this.$emit('info', message)
    }
  }
}
</script>
